import { useEffect, useMemo, useRef, useState } from 'react';
import { AppState } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Timestamp, collection, onSnapshot, query, where, type Query } from 'firebase/firestore';
import { firestore } from '../firebase';
import type { TonyteActivity } from '../types';
import { createPromotionsStore, type PromotionsSource, type PromotionsState } from '../services/promotionsStore';
import { nextEventEnd, resolvePromotionEvents, selectFeedPromotions, isPromotionLive, promotionTargetsCity, type FeedPromotion, type PublishedPromotion } from '../utils/promotions';

// Published promotions only. firestore.rules lets app users read a
// promotions/{id} doc only while `published == true`, so the query must
// carry that equality for the list to be allowed. The endAt bound keeps
// long-ended campaigns out of the payload (composite index in
// firestore.indexes.json); if that index isn't deployed yet the listener
// falls back to the equality-only query instead of failing.
const firestoreSource: PromotionsSource = {
  subscribe(onDocs, onError) {
    const ref = collection(firestore, 'promotions');
    let stop: (() => void) | null = null;
    let usedFallback = false;
    const listen = (target: Query) => {
      stop = onSnapshot(
        target,
        snapshot => onDocs(snapshot.docs.map(doc => ({ id: doc.id, data: doc.data() as Record<string, unknown> }))),
        error => {
          const code = (error as { code?: string })?.code;
          if (!usedFallback && code === 'failed-precondition') {
            usedFallback = true;
            stop?.();
            listen(query(ref, where('published', '==', true)));
            return;
          }
          onError(error);
        },
      );
    };
    listen(query(ref, where('published', '==', true), where('endAt', '>', Timestamp.fromMillis(Date.now()))));
    return () => stop?.();
  },
};

const promotionsStore = createPromotionsStore({
  source: firestoreSource,
  storage: AsyncStorage,
  foreground: {
    onForeground(callback) {
      let last = AppState.currentState;
      const subscription = AppState.addEventListener('change', next => {
        if (next === 'active' && last !== 'active') callback();
        last = next;
      });
      return () => subscription.remove();
    },
  },
});

export function usePromotionsState(): PromotionsState {
  const [state, setState] = useState(promotionsStore.getState());
  useEffect(() => {
    const unsubscribe = promotionsStore.subscribe(setState);
    promotionsStore.acquire();
    setState(promotionsStore.getState());
    return () => {
      unsubscribe();
      promotionsStore.release();
    };
  }, []);
  return state;
}

// Event end times are wall-clock facts too: a collection whose last event
// ends while the feed is open should disappear without a refresh. A cheap
// once-a-minute local re-check (no network) — only while there is something
// to re-check.
const EVENT_RECHECK_MS = 60 * 1000;

function useMinuteClock(enabled: boolean, baseClock: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setNow(Date.now());
  }, [baseClock]);
  useEffect(() => {
    if (!enabled) return undefined;
    const timer = setInterval(() => setNow(Date.now()), EVENT_RECHECK_MS);
    return () => clearInterval(timer);
  }, [enabled]);
  return now;
}

function sameSelection(a: FeedPromotion[], b: FeedPromotion[]) {
  return a.length === b.length && a.every((entry, i) => {
    const other = b[i];
    return entry.promotion === other.promotion
      && entry.events.length === other.events.length
      && entry.events.every((event, j) => event === other.events[j]);
  });
}

// Returns the previous array when nothing visible changed, so the feed
// header (and the cards' memo) don't re-render on every clock tick.
export function useFeedPromotions(pool: TonyteActivity[], cityKey: string | null): FeedPromotion[] {
  const { promotions, clock } = usePromotionsState();
  const now = useMinuteClock(promotions.length > 0, clock);
  const previous = useRef<FeedPromotion[]>([]);
  const [eventTick, setEventTick] = useState(0);
  const selection = useMemo(() => {
    const next = selectFeedPromotions(promotions, pool, { now: Math.max(now, clock, eventTick), cityKey });
    if (sameSelection(previous.current, next)) return previous.current;
    previous.current = next;
    return next;
  }, [promotions, pool, now, clock, eventTick, cityKey]);
  // Wake exactly when the next shown event ends (it drops out; an emptied
  // collection disappears right then).
  useEffect(() => {
    const at = nextEventEnd(selection, Date.now());
    if (at === null) return undefined;
    const timer = setTimeout(() => setEventTick(Date.now()), Math.min(2 ** 31 - 1, Math.max(0, at - Date.now()) + 250));
    return () => clearTimeout(timer);
  }, [selection]);
  return selection;
}

export type PromotionDetailState =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | { status: 'ready'; promotion: PublishedPromotion; events: TonyteActivity[] };

// A collection opened from the feed or an old link. Ended, unpublished,
// other-city or now-empty collections resolve to 'unavailable' rather than
// an empty page.
export function usePromotionDetail(promotionId: string, pool: TonyteActivity[], poolLoading: boolean, cityKey: string | null): PromotionDetailState {
  const { promotions, loaded, fromCache, clock } = usePromotionsState();
  const now = useMinuteClock(true, clock);
  return useMemo(() => {
    const t = Math.max(now, clock);
    const promotion = promotions.find(p => p.id === promotionId);
    if (!promotion) return !loaded || (fromCache && !promotions.length) ? { status: 'loading' } : { status: 'unavailable' };
    if (!isPromotionLive(promotion, t) || !promotionTargetsCity(promotion, cityKey)) return { status: 'unavailable' };
    const events = resolvePromotionEvents(promotion, pool, { now: t, cityKey });
    if (!events.length) return poolLoading ? { status: 'loading' } : { status: 'unavailable' };
    return { status: 'ready', promotion, events };
  }, [promotions, loaded, fromCache, promotionId, pool, poolLoading, now, clock, cityKey]);
}
